// Contrat de la console add-ons : les révisions viennent toujours du serveur.
import type {
  DroitAddon,
  EtatPublicationAddon,
  ModeAddon,
  ModuleAddon,
  CategorieModule,
  LignePrixAddon,
  VersionPrixAddon,
  FicheFacturationSaisie,
} from '@artosera/shared';
import { requete, modeDemo, ApiError } from './api.js';
import { MODULES_ADDONS, REGISTRE_MODULES } from '@artosera/shared';

export interface DroitAddonVue extends DroitAddon {
  depuisLe: string;
  jusquAu: string | null;
  motif: string;
  attribuePar: string;
  modifieLe: string;
}
export interface AddonGalerieVue {
  addon: ModuleAddon;
  categorie: CategorieModule;
  disponible: boolean;
  publication: EtatPublicationAddon;
  droit: DroitAddonVue | null;
  effectif: boolean;
  etatDroit: 'absent' | 'en_cours' | 'echu' | 'a_venir';
}
export interface AddonsGalerie {
  remises?: RemiseVue[];
  revisionAddons: number;
  addons: AddonGalerieVue[];
}
export interface PublicationVue {
  addon: ModuleAddon;
  etat: EtatPublicationAddon;
  motif: string | null;
  auteur: string | null;
  modifieLe: string | null;
}
export interface AddonsGlobaux {
  addons: {
    addon: ModuleAddon;
    categorie: CategorieModule;
    disponible: boolean;
    publication: PublicationVue;
    enCours: Record<ModeAddon, number>;
  }[];
  historique: PublicationVue[];
  echeances: (DroitAddonVue & { galerieId: string; galerie: string })[];
}
export interface CorpsDroitAddon {
  mode: ModeAddon;
  depuisLe: string;
  jusquAu: string | null;
  motif: string;
  revisionAddons: number;
}

/** Le mode démo présente le catalogue fermé, sans appel réseau ni attribution. */
const catalogueDemo = () =>
  MODULES_ADDONS.map((addon) => ({
    addon,
    categorie: REGISTRE_MODULES[addon].categorie!,
    disponible: false,
  }));
function exigerModeReel() {
  if (modeDemo())
    throw new ApiError(403, 'module.indisponible', 'Add-ons indisponibles en démonstration.');
}
export interface PrixAddonVue extends LignePrixAddon {
  enVigueur: VersionPrixAddon | null;
  futures: VersionPrixAddon[];
}
export interface FicheFacturationVue {
  ficheFacturation:
    | (FicheFacturationSaisie & {
        tvaVerifieeLe?: string;
        tvaVerifieePar?: 'verificateur' | 'console';
        modifieLe: string;
        modifieParId: string;
      })
    | null;
  revisionFicheFacturation: number;
}
export const marketplaceConsoleApi = {
  prix: (): Promise<{ prix: PrixAddonVue[] }> =>
    modeDemo() ? Promise.resolve({ prix: [] }) : requete('/v1/console/addons/prix'),
  ajouterPrix: (addon: ModuleAddon, corps: unknown): Promise<unknown> => {
    exigerModeReel();
    return requete(`/v1/console/addons/${addon}/prix`, { methode: 'POST', corps });
  },
  supprimerPrix: (
    addon: ModuleAddon,
    version: number,
    corps: { motif: string; revision: number },
  ): Promise<unknown> => {
    exigerModeReel();
    return requete(`/v1/console/addons/${addon}/prix/${version}`, { methode: 'DELETE', corps });
  },
  fiche: (id: string): Promise<FicheFacturationVue> =>
    modeDemo()
      ? Promise.resolve({ ficheFacturation: null, revisionFicheFacturation: 0 })
      : requete(`/v1/console/galleries/${encodeURIComponent(id)}/fiche-facturation`),
  ecrireFiche: (id: string, corps: unknown): Promise<FicheFacturationVue> => {
    exigerModeReel();
    return requete(`/v1/console/galleries/${encodeURIComponent(id)}/fiche-facturation`, {
      methode: 'PUT',
      corps,
    });
  },
  verifierTva: (
    id: string,
    corps: { motif: string; revisionFicheFacturation: number },
  ): Promise<FicheFacturationVue> => {
    exigerModeReel();
    return requete(
      `/v1/console/galleries/${encodeURIComponent(id)}/fiche-facturation/verifier-tva`,
      { methode: 'POST', corps },
    );
  },
};
export const addonsConsoleApi = {
  galerie: (id: string): Promise<AddonsGalerie> =>
    modeDemo()
      ? Promise.resolve({
          revisionAddons: 0,
          addons: catalogueDemo().map((a) => ({
            ...a,
            publication: 'essai',
            droit: null,
            effectif: false,
            etatDroit: 'absent',
          })),
        })
      : requete(`/v1/console/galleries/${encodeURIComponent(id)}/addons`),
  globale: (): Promise<AddonsGlobaux> =>
    modeDemo()
      ? Promise.resolve({
          addons: catalogueDemo().map((a) => ({
            ...a,
            publication: {
              addon: a.addon,
              etat: 'essai',
              motif: null,
              auteur: null,
              modifieLe: null,
            },
            enCours: { achat: 0, location: 0, essai: 0, offert: 0 },
          })),
          historique: [],
          echeances: [],
        })
      : requete('/v1/console/addons'),
  ecrire: (id: string, addon: ModuleAddon, corps: CorpsDroitAddon): Promise<AddonsGalerie> => {
    exigerModeReel();
    return requete(`/v1/console/galleries/${encodeURIComponent(id)}/addons/${addon}`, {
      methode: 'PUT',
      corps,
    });
  },
  retirer: (
    id: string,
    addon: ModuleAddon,
    corps: { motif: string; revisionAddons: number },
  ): Promise<AddonsGalerie> => {
    exigerModeReel();
    return requete(`/v1/console/galleries/${encodeURIComponent(id)}/addons/${addon}`, {
      methode: 'DELETE',
      corps,
    });
  },
  publier: (
    addon: ModuleAddon,
    corps: { etat: EtatPublicationAddon; motif: string },
  ): Promise<AddonsGlobaux> => {
    exigerModeReel();
    return requete(`/v1/console/addons/${addon}/publication`, { methode: 'PUT', corps });
  },
};

export interface ReglagesVenio {
  emetteur: {
    raisonSociale: string;
    formeJuridique?: string;
    capitalCentimes?: number;
    siren?: string;
    siret?: string;
    tvaIntracom?: string;
    rcs?: string;
    adresse: { ligne1: string; ligne2?: string; codePostal: string; ville: string; pays: string };
    courriel?: string;
    iban?: string;
    bic?: string;
    banque?: string;
  };
  delaiPaiementJours: number;
  prefixeSerie: string;
  prefixeSerieAvoir: string;
  revision: number;
}
export interface RemiseVue {
  retiree: boolean;
  addon: ModuleAddon;
  pointsDeBase: number;
  jusquAu: string | null;
  motif: string;
  revision: number;
}
export const facturationVenioApi = {
  lire: (): Promise<{ reglages: ReglagesVenio | null }> =>
    modeDemo() ? Promise.resolve({ reglages: null }) : requete('/v1/console/addons/facturation'),
  ecrire: (corps: ReglagesVenio): Promise<{ reglages: ReglagesVenio }> => {
    exigerModeReel();
    return requete('/v1/console/addons/facturation', { methode: 'PUT', corps });
  },
  remise: (
    id: string,
    addon: ModuleAddon,
    corps: { pointsDeBase: number; jusquAu: string | null; motif: string; revision: number },
  ) => {
    exigerModeReel();
    return requete(`/v1/console/galleries/${encodeURIComponent(id)}/addons/${addon}/remise`, {
      methode: 'PUT',
      corps,
    });
  },
  retirerRemise: (id: string, addon: ModuleAddon, corps: { revision: number; motif: string }) => {
    exigerModeReel();
    return requete(`/v1/console/galleries/${encodeURIComponent(id)}/addons/${addon}/remise`, {
      methode: 'DELETE',
      corps,
    });
  },
};
